import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Upload, User, Phone, Mail, Trash2, Users, UserPlus, Link, Download, Printer, Send, FileSpreadsheet, AlertCircle, CheckCircle, Pencil, MessageSquare, Loader2, Inbox, Sparkles, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactCardsPage({ user, onLogout }) {
  const [coachContacts, setCoachContacts] = useState([]);
  const [parentContacts, setParentContacts] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showQuickAddParentDialog, setShowQuickAddParentDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [contactType, setContactType] = useState('coach');
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    type: 'coach'
  });
  const [quickAddParent, setQuickAddParent] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    player_id: ''
  });
  const [bulkImportResults, setBulkImportResults] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', phone: '', email: '', player_id: '' });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // ---- SMS Blast (Twilio bulk send) ----
  const [showSmsDialog, setShowSmsDialog] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSelectedIds, setSmsSelectedIds] = useState([]);
  const [smsResults, setSmsResults] = useState(null);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [smsStatus, setSmsStatus] = useState(null);

  // ---- SMS Templates ----
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  // ---- Single-Recipient Quick Text (from parent card) ----
  const [quickTextContact, setQuickTextContact] = useState(null);
  const [quickTextMessage, setQuickTextMessage] = useState('');
  const [isSendingQuickText, setIsSendingQuickText] = useState(false);

  // ---- Reply Inbox ----
  const [showInboxDialog, setShowInboxDialog] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Track any in-flight polling timers so we can clean up on unmount.
  const pollTimersRef = useRef([]);
  useEffect(() => () => {
    pollTimersRef.current.forEach(({ interval, timeout }) => {
      clearInterval(interval);
      clearTimeout(timeout);
    });
    pollTimersRef.current = [];
  }, []);
  const fileInputRef = useRef(null);
  const bulkImportRef = useRef(null);

  useEffect(() => {
    loadContacts();
    loadPlayers();
    loadTemplates();
    loadUnreadCount();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/messaging/templates/sms?team_id=${user.team_id}`);
      setSmsTemplates(res.data || []);
    } catch (e) {
      // silent
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/messaging/sms/inbound/unread-count?team_id=${user.team_id}`);
      setUnreadCount(res.data?.unread || 0);
    } catch (e) {
      // silent
    }
  };

  const loadContacts = async () => {
    try {
      const [coachRes, parentRes] = await Promise.all([
        axios.get(`${API}/contacts?team_id=${user.team_id}&type=coach`),
        axios.get(`${API}/contacts?team_id=${user.team_id}&type=parent`)
      ]);
      setCoachContacts(coachRes.data);
      setParentContacts(parentRes.data);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  };

  const loadPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players?team_id=${user.team_id}`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to load players');
    }
  };

  // Get players without parent contact linked
  const getPlayersWithoutParent = () => {
    const linkedPlayerIds = parentContacts.map(c => c.player_id).filter(Boolean);
    return players.filter(p => !linkedPlayerIds.includes(p.id));
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contacts`, {
        ...newContact,
        type: contactType,
        team_id: user.team_id,
        created_by: user.id
      });
      toast.success(`${contactType === 'coach' ? 'Coach' : 'Parent'} contact added!`);
      setShowAddDialog(false);
      setNewContact({ first_name: '', last_name: '', phone: '', email: '', type: 'coach' });
      loadContacts();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Contact deleted');
      loadContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setEditForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      player_id: contact.player_id || '',
    });
  };

  const saveContactEdit = async (e) => {
    e.preventDefault();
    if (!editingContact) return;
    setIsSavingEdit(true);
    try {
      const payload = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        phone: editForm.phone.trim(),
        email: editForm.email.trim(),
      };
      if (editingContact.type === 'parent') {
        payload.player_id = editForm.player_id || null;
      }
      await axios.put(`${API}/contacts/${editingContact.id}`, payload);
      toast.success('Contact updated');
      setEditingContact(null);
      loadContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleImportVCard = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('team_id', user.team_id);
    formData.append('type', contactType);
    formData.append('created_by', user.id);

    try {
      const response = await axios.post(`${API}/contacts/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.imported} contacts imported!`);
      loadContacts();
    } catch (error) {
      toast.error('Failed to import contacts');
    }
    e.target.value = '';
  };

  const handleQuickAddParent = async (e) => {
    e.preventDefault();
    if (!quickAddParent.player_id) {
      toast.error('Please select a player to link this parent to');
      return;
    }
    
    try {
      // Update the player with parent info - this will auto-create/update the contact
      await axios.put(`${API}/players/${quickAddParent.player_id}`, {
        parent_first_name: quickAddParent.first_name,
        parent_last_name: quickAddParent.last_name,
        parent_phone: quickAddParent.phone,
        parent_email: quickAddParent.email
      });
      
      toast.success('Parent contact added and linked to player!');
      setShowQuickAddParentDialog(false);
      setQuickAddParent({ first_name: '', last_name: '', phone: '', email: '', player_id: '' });
      loadContacts();
      loadPlayers();
    } catch (error) {
      toast.error('Failed to add parent contact');
    }
  };

  // Find linked player name for a parent contact
  const getLinkedPlayerName = (contact) => {
    if (!contact.player_id) return null;
    const player = players.find(p => p.id === contact.player_id);
    return player ? `${player.first_name} ${player.last_name}` : null;
  };

  // Bulk import parents from CSV
  const handleBulkImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvText = event.target.result;
        const lines = csvText.split('\n').filter(line => line.trim());
        const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
        
        // Expected columns: player_name OR jersey_number, parent_first_name, parent_last_name, parent_phone, parent_email
        const results = { matched: [], unmatched: [], errors: [] };
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx] || '';
          });
          
          // Try to match player by name or jersey number
          let matchedPlayer = null;
          
          if (row.player_name) {
            const nameParts = row.player_name.toLowerCase().split(' ');
            matchedPlayer = players.find(p => {
              const playerFullName = `${p.first_name} ${p.last_name}`.toLowerCase();
              return playerFullName.includes(nameParts[0]) && 
                     (nameParts.length === 1 || playerFullName.includes(nameParts[nameParts.length - 1]));
            });
          }
          
          if (!matchedPlayer && row.jersey_number) {
            matchedPlayer = players.find(p => String(p.jersey_number) === String(row.jersey_number));
          }
          
          if (matchedPlayer) {
            results.matched.push({
              player: matchedPlayer,
              parent: {
                first_name: row.parent_first_name || row.first_name || '',
                last_name: row.parent_last_name || row.last_name || '',
                phone: row.parent_phone || row.phone || '',
                email: row.parent_email || row.email || ''
              }
            });
          } else {
            results.unmatched.push({
              row: i + 1,
              data: row.player_name || row.jersey_number || 'Unknown'
            });
          }
        }
        
        setBulkImportResults(results);
        setShowBulkImportDialog(true);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const confirmBulkImport = async () => {
    if (!bulkImportResults?.matched?.length) return;
    
    let successCount = 0;
    for (const match of bulkImportResults.matched) {
      try {
        await axios.put(`${API}/players/${match.player.id}`, {
          parent_first_name: match.parent.first_name,
          parent_last_name: match.parent.last_name,
          parent_phone: match.parent.phone,
          parent_email: match.parent.email
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to update player ${match.player.first_name}`);
      }
    }
    
    toast.success(`Successfully imported ${successCount} parent contacts!`);
    setShowBulkImportDialog(false);
    setBulkImportResults(null);
    loadContacts();
    loadPlayers();
  };

  // Export contacts to CSV
  const handleExportCSV = (type) => {
    const contacts = type === 'coach' ? coachContacts : parentContacts;
    if (contacts.length === 0) {
      toast.error(`No ${type} contacts to export`);
      return;
    }
    
    const headers = ['First Name', 'Last Name', 'Phone', 'Email'];
    if (type === 'parent') headers.push('Linked Player');
    
    const csvContent = [
      headers.join(','),
      ...contacts.map(c => {
        const row = [
          c.first_name || '',
          c.last_name || '',
          c.phone || '',
          c.email || ''
        ];
        if (type === 'parent') {
          row.push(getLinkedPlayerName(c) || '');
        }
        return row.map(v => `"${v}"`).join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_contacts.csv`;
    a.click();
    toast.success(`${type === 'coach' ? 'Coach' : 'Parent'} contacts exported!`);
  };

  // Print contacts
  const handlePrint = (type) => {
    const contacts = type === 'coach' ? coachContacts : parentContacts;
    if (contacts.length === 0) {
      toast.error(`No ${type} contacts to print`);
      return;
    }
    
    const printWindow = window.open('', '_blank');
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${type === 'coach' ? 'Coaching Staff' : 'Parent'} Contacts - PAC</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1d4ed8; border-bottom: 2px solid #1d4ed8; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #1d4ed8; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .header { display: flex; justify-content: space-between; align-items: center; }
            .date { color: #666; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🏀 PAC - ${type === 'coach' ? 'Coaching Staff' : 'Parent'} Contacts</h1>
            <span class="date">Generated: ${new Date().toLocaleDateString()}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                ${type === 'parent' ? '<th>Linked Player</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${contacts.map(c => `
                <tr>
                  <td>${c.first_name} ${c.last_name}</td>
                  <td>${c.phone || '-'}</td>
                  <td>${c.email || '-'}</td>
                  ${type === 'parent' ? `<td>${getLinkedPlayerName(c) || '-'}</td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p style="margin-top: 20px; color: #666; font-size: 12px;">
            Total: ${contacts.length} contact(s)
          </p>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  // Email contacts
  const handleEmailContacts = async (type) => {
    if (!emailRecipient) {
      toast.error('Please enter an email address');
      return;
    }
    
    const contacts = type === 'coach' ? coachContacts : parentContacts;
    if (contacts.length === 0) {
      toast.error(`No ${type} contacts to email`);
      return;
    }
    
    setIsSendingEmail(true);
    try {
      await axios.post(`${API}/contacts/email`, {
        team_id: user.team_id,
        type,
        recipient_email: emailRecipient,
        contacts: contacts.map(c => ({
          ...c,
          linked_player: type === 'parent' ? getLinkedPlayerName(c) : null
        }))
      });
      toast.success(`Contact list sent to ${emailRecipient}!`);
      setShowEmailDialog(false);
      setEmailRecipient('');
    } catch (error) {
      toast.error('Failed to send email. Check if email is configured.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Download CSV template for bulk import
  const downloadTemplate = () => {
    const template = 'player_name,jersey_number,parent_first_name,parent_last_name,parent_phone,parent_email\n"John Smith",23,"Jane","Smith","+1 555-123-4567","jane.smith@email.com"\n"Mike Johnson",10,"Sarah","Johnson","+1 555-987-6543","sarah.j@email.com"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'parent_import_template.csv';
    a.click();
    toast.success('Template downloaded!');
  };

  // ---- SMS Blast handlers ----
  const parentsWithPhones = parentContacts.filter((c) => (c.phone || '').trim().length > 0);

  const openSmsBlast = async () => {
    setSmsResults(null);
    setSmsMessage('');
    setSmsSelectedIds(parentsWithPhones.map((c) => c.id));
    setShowSmsDialog(true);
    if (!smsStatus) {
      try {
        const res = await axios.get(`${API}/messaging/sms-status`);
        setSmsStatus(res.data);
      } catch (e) {
        setSmsStatus({ sms_available: false });
      }
    }
  };

  const toggleSmsRecipient = (id) => {
    setSmsSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSmsSelectAll = () => {
    if (smsSelectedIds.length === parentsWithPhones.length) {
      setSmsSelectedIds([]);
    } else {
      setSmsSelectedIds(parentsWithPhones.map((c) => c.id));
    }
  };

  const sendSmsBlast = async () => {
    if (!smsMessage.trim()) return toast.error('Type a message first');
    if (smsSelectedIds.length === 0) return toast.error('Pick at least one recipient');
    setIsSendingSms(true);
    setSmsResults(null);
    try {
      const res = await axios.post(`${API}/messaging/sms/bulk`, {
        team_id: user.team_id,
        message: smsMessage.trim(),
        recipient_ids: smsSelectedIds,
      });
      setSmsResults(res.data);
      const sent = res.data?.sent_count || 0;
      const failed = res.data?.failed_count || 0;
      if (failed === 0) {
        toast.success(`SMS sent to ${sent} famil${sent === 1 ? 'y' : 'ies'}`);
      } else {
        toast.warning(`Sent to ${sent}, ${failed} failed — see details below`);
      }
      // kick off delivery-status polling for each successful send
      startDeliveryPolling(res.data?.results || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send SMS blast');
    } finally {
      setIsSendingSms(false);
    }
  };

  // ---- Delivery Tracking: poll Twilio /sms/{sid}/status every 5s until terminal ----
  const TERMINAL_STATUSES = ['delivered', 'undelivered', 'failed'];
  const startDeliveryPolling = (results) => {
    const sids = (results || []).filter((r) => r.success && r.message_sid).map((r) => r.message_sid);
    if (sids.length === 0) return;
    const interval = setInterval(async () => {
      let allTerminal = true;
      await Promise.all(sids.map(async (sid) => {
        try {
          const res = await axios.get(`${API}/messaging/sms/${sid}/status`);
          const status = res.data?.status;
          setSmsResults((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              results: prev.results.map((r) => r.message_sid === sid ? { ...r, delivery_status: status, delivery_error: res.data?.error_message || null } : r),
            };
          });
          if (!TERMINAL_STATUSES.includes(status)) allTerminal = false;
        } catch (e) {
          // 404/network — keep polling but don't spam
        }
      }));
      if (allTerminal) {
        clearInterval(interval);
        clearTimeout(timeout);
        pollTimersRef.current = pollTimersRef.current.filter((t) => t.interval !== interval);
      }
    }, 5000);
    // Safety: stop after 3 minutes regardless
    const timeout = setTimeout(() => {
      clearInterval(interval);
      pollTimersRef.current = pollTimersRef.current.filter((t) => t.interval !== interval);
    }, 180000);
    pollTimersRef.current.push({ interval, timeout });
  };

  // ---- Templates ----
  const applyTemplate = (templateId) => {
    const t = smsTemplates.find((x) => x.id === templateId);
    if (t) setSmsMessage(t.body);
  };

  const openSaveTemplateDialog = () => {
    if (!smsMessage.trim()) return toast.error('Type a message first');
    setNewTemplateName('');
    setShowSaveTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!newTemplateName.trim()) return toast.error('Give the template a name');
    setIsSavingTemplate(true);
    try {
      await axios.post(`${API}/messaging/templates/sms`, {
        team_id: user.team_id,
        name: newTemplateName.trim(),
        body: smsMessage.trim(),
      });
      toast.success('Template saved');
      setShowSaveTemplateDialog(false);
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId, e) => {
    if (e) e.stopPropagation();
    if (templateId.startsWith('default-')) return toast.error('Starter templates cannot be deleted');
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/messaging/templates/sms/${templateId}`);
      loadTemplates();
      toast.success('Template deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // ---- Single-recipient Quick Text (from parent card) ----
  const openQuickText = (contact) => {
    setQuickTextContact(contact);
    setQuickTextMessage('');
  };

  const sendQuickText = async () => {
    if (!quickTextContact) return;
    if (!quickTextMessage.trim()) return toast.error('Type a message first');
    setIsSendingQuickText(true);
    try {
      const personalized = quickTextMessage
        .replaceAll('{name}', `${quickTextContact.first_name || ''}`.trim() || 'there');
      const res = await axios.post(`${API}/messaging/sms/send`, {
        to_phone: quickTextContact.phone,
        message: personalized,
      });
      if (res.data?.success) {
        toast.success(`Text sent to ${quickTextContact.first_name || 'contact'}`);
      } else {
        toast.error('Send failed');
      }
      setQuickTextContact(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send text');
    } finally {
      setIsSendingQuickText(false);
    }
  };

  // ---- Reply Inbox ----
  const openInbox = async () => {
    setShowInboxDialog(true);
    try {
      const res = await axios.get(`${API}/messaging/sms/inbound?team_id=${user.team_id}`);
      setInboxMessages(res.data || []);
    } catch (e) {
      setInboxMessages([]);
    }
  };

  const markInboxRead = async (msgId) => {
    try {
      await axios.put(`${API}/messaging/sms/inbound/${msgId}/read`);
      setInboxMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, read: true } : m));
      loadUnreadCount();
    } catch (e) {
      // silent
    }
  };

  const replyFromInbox = (msg) => {
    // Prefer matched contact if we know it
    const contact = msg.contact_id
      ? parentContacts.find((c) => c.id === msg.contact_id) || coachContacts.find((c) => c.id === msg.contact_id)
      : null;
    if (contact) {
      openQuickText(contact);
    } else {
      openQuickText({ first_name: '', last_name: '', phone: msg.from_phone, id: 'ad-hoc' });
    }
    setShowInboxDialog(false);
    if (!msg.read) markInboxRead(msg.id);
  };

  const ContactCard = ({ contact, onDelete, onEdit, linkedPlayer }) => (
    <Card className="border-2 hover:border-primary/50 transition-colors" data-testid={`contact-${contact.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{contact.first_name} {contact.last_name}</h3>
              {linkedPlayer && (
                <p className="text-xs text-primary flex items-center gap-1 mb-1">
                  <Link className="h-3 w-3" /> Parent of {linkedPlayer}
                </p>
              )}
              {contact.phone && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {contact.phone}
                </p>
              )}
              {contact.email && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" /> {contact.email}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {contact.type === 'parent' && contact.phone && (
              <Button
                variant="ghost"
                size="icon"
                className="text-orange-500 hover:text-orange-600"
                onClick={() => openQuickText(contact)}
                data-testid={`text-parent-btn-${contact.id}`}
                title="Text this parent"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-primary hover:text-primary"
              onClick={() => onEdit(contact)}
              data-testid={`edit-contact-${contact.id}`}
              title="Edit contact"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(contact.id)}
              data-testid={`delete-contact-${contact.id}`}
              title="Delete contact"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="contact-cards-page">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Contact Cards
            </h1>
            <p className="text-muted-foreground mt-2">Manage coaching staff and parent contacts</p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            {/* Hidden file inputs */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportVCard}
              accept=".vcf,.csv"
              className="hidden"
            />
            <input
              type="file"
              ref={bulkImportRef}
              onChange={handleBulkImportCSV}
              accept=".csv"
              className="hidden"
            />
            
            {/* Export/Print/Email Dropdown */}
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={openInbox}
                data-testid="inbox-btn"
                title="Reply Inbox — parent replies to your Twilio number"
                className="relative"
              >
                <Inbox className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="inbox-unread-badge">
                    {unreadCount}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportCSV('parent')}
                data-testid="export-csv-btn"
                title="Export to CSV"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePrint('parent')}
                data-testid="print-btn"
                title="Print"
              >
                <Printer className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEmailDialog(true)}
                data-testid="email-btn"
                title="Email"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Bulk Import Parents */}
            <Button
              variant="outline"
              onClick={() => bulkImportRef.current?.click()}
              data-testid="bulk-import-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              BULK IMPORT
            </Button>
            
            {/* Quick Add Parent Dialog */}
            <Dialog open={showQuickAddParentDialog} onOpenChange={setShowQuickAddParentDialog}>
              <DialogTrigger asChild>
                <Button variant="secondary" data-testid="quick-add-parent-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  QUICK ADD
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Quick Add Parent</DialogTitle>
                  <p className="text-sm text-muted-foreground">Add a parent contact and link them to a player</p>
                </DialogHeader>
                <form onSubmit={handleQuickAddParent} className="space-y-4">
                  <div>
                    <Label>Link to Player *</Label>
                    <Select 
                      value={quickAddParent.player_id} 
                      onValueChange={(value) => setQuickAddParent({ ...quickAddParent, player_id: value })}
                    >
                      <SelectTrigger data-testid="quick-add-player-select">
                        <SelectValue placeholder="Select a player..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getPlayersWithoutParent().length === 0 ? (
                          <SelectItem value="none" disabled>All players have parents linked</SelectItem>
                        ) : (
                          getPlayersWithoutParent().map((player) => (
                            <SelectItem key={player.id} value={player.id}>
                              #{player.jersey_number || '?'} {player.first_name} {player.last_name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getPlayersWithoutParent().length} player(s) without parent contact
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Parent First Name *</Label>
                      <Input
                        value={quickAddParent.first_name}
                        onChange={(e) => setQuickAddParent({ ...quickAddParent, first_name: e.target.value })}
                        required
                        data-testid="quick-add-first-name"
                      />
                    </div>
                    <div>
                      <Label>Parent Last Name *</Label>
                      <Input
                        value={quickAddParent.last_name}
                        onChange={(e) => setQuickAddParent({ ...quickAddParent, last_name: e.target.value })}
                        required
                        data-testid="quick-add-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      value={quickAddParent.phone}
                      onChange={(e) => setQuickAddParent({ ...quickAddParent, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      data-testid="quick-add-phone"
                    />
                  </div>
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={quickAddParent.email}
                      onChange={(e) => setQuickAddParent({ ...quickAddParent, email: e.target.value })}
                      data-testid="quick-add-email"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    data-testid="quick-add-submit-btn"
                    disabled={getPlayersWithoutParent().length === 0}
                  >
                    <Link className="h-4 w-4 mr-2" />
                    ADD & LINK PARENT
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-contact-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  ADD CONTACT
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl uppercase">Add Contact</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddContact} className="space-y-4">
                  <div>
                    <Label>Contact Type</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        variant={contactType === 'coach' ? 'default' : 'outline'}
                        onClick={() => setContactType('coach')}
                        className="flex-1"
                      >
                        Coach
                      </Button>
                      <Button
                        type="button"
                        variant={contactType === 'parent' ? 'default' : 'outline'}
                        onClick={() => setContactType('parent')}
                        className="flex-1"
                      >
                        Parent
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={newContact.first_name}
                        onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                        required
                        data-testid="contact-first-name"
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={newContact.last_name}
                        onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                        required
                        data-testid="contact-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      data-testid="contact-email"
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-contact-btn">
                    ADD CONTACT
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="coaches" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="coaches" data-testid="coaches-tab">
              <Users className="h-4 w-4 mr-2" />
              Coaching Staff ({coachContacts.length})
            </TabsTrigger>
            <TabsTrigger value="parents" data-testid="parents-tab">
              <Users className="h-4 w-4 mr-2" />
              Parents ({parentContacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="coaches">
            {coachContacts.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mt-4">No coaching staff contacts yet</p>
                  <Button className="mt-4" onClick={() => { setContactType('coach'); setShowAddDialog(true); }}>
                    ADD FIRST COACH
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {coachContacts.map((contact) => (
                  <ContactCard key={contact.id} contact={contact} onDelete={handleDeleteContact} onEdit={openEditContact} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="parents">
            <div className="flex items-center justify-end mb-3">
              <Button
                variant="default"
                size="sm"
                onClick={openSmsBlast}
                disabled={parentsWithPhones.length === 0}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="text-families-btn"
                title={parentsWithPhones.length === 0 ? 'No parent contacts have phone numbers yet' : 'Send SMS to selected parents via Twilio'}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Text these families ({parentsWithPhones.length})
              </Button>
            </div>
            {parentContacts.length === 0 ? (
              <Card className="border-2 border-dashed">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mt-4">No parent contacts yet</p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button variant="outline" onClick={() => setShowQuickAddParentDialog(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      QUICK ADD PARENT
                    </Button>
                    <Button onClick={() => { setContactType('parent'); setShowAddDialog(true); }}>
                      ADD FIRST PARENT
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {parentContacts.map((contact) => (
                  <ContactCard 
                    key={contact.id} 
                    contact={contact} 
                    onDelete={handleDeleteContact}
                    onEdit={openEditContact}
                    linkedPlayer={getLinkedPlayerName(contact)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Bulk Import Results Dialog */}
        <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Bulk Import Preview</DialogTitle>
              <p className="text-sm text-muted-foreground">Review matched parents before importing</p>
            </DialogHeader>
            {bulkImportResults && (
              <div className="space-y-4">
                {bulkImportResults.matched.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-semibold text-green-700">
                        {bulkImportResults.matched.length} Parents Matched
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-muted sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Player</th>
                            <th className="p-2 text-left">Parent Name</th>
                            <th className="p-2 text-left">Contact</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkImportResults.matched.map((match, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-2">
                                #{match.player.jersey_number} {match.player.first_name} {match.player.last_name}
                              </td>
                              <td className="p-2">{match.parent.first_name} {match.parent.last_name}</td>
                              <td className="p-2 text-muted-foreground text-xs">
                                {match.parent.phone || match.parent.email || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {bulkImportResults.unmatched.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                      <span className="font-semibold text-yellow-700">
                        {bulkImportResults.unmatched.length} Rows Not Matched
                      </span>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                      <p className="text-yellow-800 mb-2">Could not find players for:</p>
                      <ul className="list-disc list-inside text-yellow-700">
                        {bulkImportResults.unmatched.slice(0, 5).map((item, idx) => (
                          <li key={idx}>Row {item.row}: "{item.data}"</li>
                        ))}
                        {bulkImportResults.unmatched.length > 5 && (
                          <li>...and {bulkImportResults.unmatched.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={downloadTemplate} className="flex-1">
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  <Button 
                    onClick={confirmBulkImport} 
                    className="flex-1"
                    disabled={bulkImportResults.matched.length === 0}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Import {bulkImportResults.matched.length} Parents
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Email Contact List</DialogTitle>
              <p className="text-sm text-muted-foreground">Send the contact list to an email address</p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Recipient Email</Label>
                <Input
                  type="email"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="coach@team.com"
                  data-testid="email-recipient-input"
                />
              </div>
              <div>
                <Label>Which contacts to send?</Label>
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleEmailContacts('coach')}
                    disabled={isSendingEmail || coachContacts.length === 0}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Coaches ({coachContacts.length})
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleEmailContacts('parent')}
                    disabled={isSendingEmail || parentContacts.length === 0}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Parents ({parentContacts.length})
                  </Button>
                </div>
              </div>
              {isSendingEmail && (
                <p className="text-sm text-muted-foreground text-center">Sending...</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Contact Dialog */}
        <Dialog open={!!editingContact} onOpenChange={(v) => { if (!v) setEditingContact(null); }}>
          <DialogContent className="max-w-md" data-testid="edit-contact-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Pencil className="h-5 w-5" />
                Edit Contact
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={saveContactEdit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={editForm.first_name}
                    onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                    required
                    data-testid="edit-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={editForm.last_name}
                    onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                    required
                    data-testid="edit-last-name"
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  data-testid="edit-phone"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-email"
                />
              </div>
              {editingContact?.type === 'parent' && (
                <div>
                  <Label>Linked Player</Label>
                  <Select
                    value={editForm.player_id || 'none'}
                    onValueChange={(v) => setEditForm({ ...editForm, player_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="edit-player-select">
                      <SelectValue placeholder="Choose player" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not linked —</SelectItem>
                      {players.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setEditingContact(null)}>Cancel</Button>
                <Button type="submit" disabled={isSavingEdit} data-testid="save-contact-edit-btn">
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* SMS Blast Dialog (Twilio bulk send) */}
        <Dialog open={showSmsDialog} onOpenChange={setShowSmsDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sms-blast-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-orange-500" />
                Text these families
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Bulk SMS via Twilio. Use <code className="bg-muted px-1 rounded">{'{name}'}</code> to personalize each message with the parent&apos;s name.
              </p>
            </DialogHeader>

            {smsStatus?.sms_available === false && (
              <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="sms-unavailable-banner">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>SMS service is not configured. Add Twilio credentials in the server .env.</span>
              </div>
            )}
            {smsStatus?.sms_available && smsStatus?.is_trial_account && (
              <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="sms-trial-banner">
                <AlertCircle className="h-4 w-4 mt-0.5" />
                <span>{smsStatus.trial_note || 'Twilio trial account — SMS only reaches numbers verified in the Twilio console.'}</span>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Message</Label>
                  <div className="flex items-center gap-2">
                    {smsTemplates.length > 0 && (
                      <Select onValueChange={applyTemplate}>
                        <SelectTrigger className="h-7 w-40 text-xs" data-testid="template-select">
                          <SelectValue placeholder="Templates…" />
                        </SelectTrigger>
                        <SelectContent>
                          {smsTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id} data-testid={`template-item-${t.id}`}>
                              <span className="inline-flex items-center gap-1">
                                {t.is_default && <Sparkles className="h-3 w-3 text-amber-500" />}
                                {t.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={openSaveTemplateDialog}
                      disabled={!smsMessage.trim()}
                      data-testid="save-template-btn"
                    >
                      Save as template
                    </Button>
                  </div>
                </div>
                <Textarea
                  rows={4}
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  placeholder={"e.g. Hi {name}, tonight's practice at 6pm has been moved to 7pm. See you at the gym!"}
                  data-testid="sms-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {smsMessage.length} chars · standard SMS is 160 chars — longer sends as concatenated segments.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Recipients ({smsSelectedIds.length}/{parentsWithPhones.length})</Label>
                  <Button variant="outline" size="sm" onClick={toggleSmsSelectAll} data-testid="sms-select-all-btn">
                    {smsSelectedIds.length === parentsWithPhones.length ? 'Deselect all' : 'Select all'}
                  </Button>
                </div>
                {parentsWithPhones.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground py-4">
                    No parents have a phone number yet. Add one from the Parents tab first.
                  </p>
                ) : (
                  <div className="max-h-56 overflow-y-auto border rounded divide-y">
                    {parentsWithPhones.map((c) => {
                      const picked = smsSelectedIds.includes(c.id);
                      const linked = getLinkedPlayerName(c);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 ${picked ? 'bg-orange-50' : ''}`}
                          data-testid={`sms-recipient-row-${c.id}`}
                        >
                          <Checkbox
                            checked={picked}
                            onCheckedChange={() => toggleSmsRecipient(c.id)}
                            data-testid={`sms-recipient-checkbox-${c.id}`}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {c.first_name} {c.last_name}
                              {linked && <span className="text-xs text-muted-foreground ml-2">· parent of {linked}</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">{c.phone}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {smsResults && (
                <div className="border rounded p-3" data-testid="sms-results-panel">
                  <p className="text-sm font-semibold mb-2">
                    Result: {smsResults.sent_count} sent · {smsResults.failed_count} failed · {smsResults.total_recipients} total
                  </p>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {(smsResults.results || []).map((r, idx) => {
                      const ds = r.delivery_status;
                      const dsColor = ds === 'delivered' ? 'bg-green-100 text-green-800'
                        : (ds === 'failed' || ds === 'undelivered') ? 'bg-red-100 text-red-800'
                        : ds ? 'bg-blue-100 text-blue-800'
                        : '';
                      return (
                        <div key={idx} className={`flex items-center gap-2 ${r.success ? 'text-green-700' : 'text-red-700'}`} data-testid={`sms-result-row-${idx}`}>
                          {r.success ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0" />}
                          <span className="font-medium">{r.name}</span>
                          <span className="text-muted-foreground">— {r.phone}</span>
                          {!r.success && <span className="text-red-600">· {r.error}</span>}
                          {r.success && ds && (
                            <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 uppercase text-[10px] font-bold ${dsColor}`} data-testid={`sms-status-pill-${idx}`}>
                              <Clock className="h-3 w-3" />
                              {ds}
                            </span>
                          )}
                          {r.success && !ds && (
                            <span className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 uppercase text-[10px] font-bold bg-gray-100 text-gray-700">
                              <Clock className="h-3 w-3 animate-spin" />
                              polling…
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setShowSmsDialog(false)}>Close</Button>
                <Button
                  onClick={sendSmsBlast}
                  disabled={isSendingSms || smsSelectedIds.length === 0 || !smsMessage.trim() || smsStatus?.sms_available === false}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="send-sms-blast-btn"
                >
                  {isSendingSms ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send SMS ({smsSelectedIds.length})</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save Template Dialog */}
        <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
          <DialogContent className="max-w-md" data-testid="save-template-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Save SMS Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Template name</Label>
                <Input
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="e.g. Practice Cancelled"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label>Body preview</Label>
                <div className="bg-muted rounded p-2 text-sm max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {smsMessage}
                </div>
              </div>
              {smsTemplates.filter((t) => !t.is_default).length > 0 && (
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Your saved templates</Label>
                  <div className="space-y-1 mt-1">
                    {smsTemplates.filter((t) => !t.is_default).map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-xs bg-muted/40 rounded px-2 py-1" data-testid={`saved-template-row-${t.id}`}>
                        <span className="truncate font-medium">{t.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-500"
                          onClick={(e) => deleteTemplate(t.id, e)}
                          data-testid={`delete-template-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowSaveTemplateDialog(false)}>Cancel</Button>
                <Button
                  onClick={saveTemplate}
                  disabled={isSavingTemplate || !newTemplateName.trim()}
                  data-testid="submit-save-template-btn"
                >
                  {isSavingTemplate ? 'Saving…' : 'Save Template'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Text (single-parent) Dialog */}
        <Dialog open={!!quickTextContact} onOpenChange={(v) => { if (!v) setQuickTextContact(null); }}>
          <DialogContent className="max-w-md" data-testid="quick-text-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-orange-500" />
                Text {quickTextContact?.first_name || 'contact'}
              </DialogTitle>
              {quickTextContact?.phone && (
                <p className="text-xs text-muted-foreground">to {quickTextContact.phone}</p>
              )}
            </DialogHeader>
            <div className="space-y-3">
              {smsTemplates.length > 0 && (
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Quick template</Label>
                  <Select onValueChange={(id) => {
                    const t = smsTemplates.find((x) => x.id === id);
                    if (t) setQuickTextMessage(t.body);
                  }}>
                    <SelectTrigger className="h-8 text-xs" data-testid="quick-text-template-select">
                      <SelectValue placeholder="Pick a template…" />
                    </SelectTrigger>
                    <SelectContent>
                      {smsTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="inline-flex items-center gap-1">
                            {t.is_default && <Sparkles className="h-3 w-3 text-amber-500" />}
                            {t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Message</Label>
                <Textarea
                  rows={4}
                  value={quickTextMessage}
                  onChange={(e) => setQuickTextMessage(e.target.value)}
                  placeholder="Type a quick message…"
                  data-testid="quick-text-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">{quickTextMessage.length} chars</p>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setQuickTextContact(null)}>Cancel</Button>
                <Button
                  onClick={sendQuickText}
                  disabled={isSendingQuickText || !quickTextMessage.trim()}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="send-quick-text-btn"
                >
                  {isSendingQuickText ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reply Inbox Dialog */}
        <Dialog open={showInboxDialog} onOpenChange={setShowInboxDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inbox-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
                <Inbox className="h-5 w-5 text-orange-500" />
                Reply Inbox
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Parent replies to your Twilio number. Requires the webhook at
                <code className="bg-muted px-1 mx-1 rounded">/api/messaging/sms/inbound</code>
                configured on your Twilio number.
              </p>
            </DialogHeader>
            {inboxMessages.length === 0 ? (
              <p className="text-sm italic text-muted-foreground py-6 text-center">
                No replies yet. When a parent texts your Twilio number, replies will appear here.
              </p>
            ) : (
              <div className="divide-y border rounded max-h-[55vh] overflow-y-auto">
                {inboxMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 hover:bg-muted/50 cursor-pointer ${msg.read ? '' : 'bg-orange-50'}`}
                    onClick={() => replyFromInbox(msg)}
                    data-testid={`inbox-msg-${msg.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold flex items-center gap-2">
                          {!msg.read && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                          {msg.contact_name || msg.from_phone}
                          <span className="text-xs text-muted-foreground font-normal">{msg.from_phone}</span>
                        </p>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{msg.body}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {msg.received_at ? new Date(msg.received_at).toLocaleString() : ''}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); replyFromInbox(msg); }}
                        data-testid={`reply-inbox-${msg.id}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5 mr-1" />
                        Reply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
